import { Vector2, EquipmentItem, EquipmentSlot, ItemRarity } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { AssetLoader } from '@client/core/AssetLoader';
import { GameItem, getItemColor, CurrencyItem } from '@shared/items/Item';
import { LootFilter } from '@client/systems/LootFilter';
import { getParticleSystem } from '@client/effects/ParticleSystem';
import { getProceduralIcon } from '@client/core/ProceduralItemIcons';

/** Loot filter settings - controls which item rarities are displayed */
export interface LootFilterSettings {
  showNormal: boolean;
  showMagic: boolean;
  showRare: boolean;
  showSet: boolean;
  showLegendary: boolean;
  showCurrency: boolean;
  showQuest: boolean;
  minItemLevel: number;
}

export class DroppedItem {
  /** Shared frame time — set once per frame from the render loop to avoid Date.now() per item */
  static frameTime: number = 0;
  static setFrameTime(t: number): void { DroppedItem.frameTime = t; }

  /** Shared asset loader for bitmap item icons */
  static assetLoader: AssetLoader | null = null;
  static setAssetLoader(loader: AssetLoader): void { DroppedItem.assetLoader = loader; }

  public position: Vector2;
  public item: GameItem;
  public pickupRadius: number = 40;

  // Animation
  private bobOffset: number = 0;

  // Physics (for drop animation)
  private velocity: Vector2 = { x: 0, y: 0 };
  private grounded: boolean = false;
  private groundY: number;

  // Pickup animation
  public isBeingCollected: boolean = false;
  private collectTimer: number = 0;
  private collectDuration: number = 0.2;
  private collectStartPos: Vector2 = { x: 0, y: 0 };
  private collectTargetPos: Vector2 = { x: 0, y: 0 };
  private collectScale: number = 1.0;

  /** Static loot filter settings shared across all dropped items */
  static filterSettings: LootFilterSettings = {
    showNormal: true,
    showMagic: true,
    showRare: true,
    showSet: true,
    showLegendary: true,
    showCurrency: true,
    showQuest: true,
    minItemLevel: 0,
  };

  constructor(x: number, y: number, item: GameItem) {
    this.position = { x, y };
    this.groundY = y;
    this.item = item;

    // Random spawn velocity for scatter effect
    const angle = Math.random() * Math.PI * 2;
    const force = 50 + Math.random() * 100;
    this.velocity = {
      x: Math.cos(angle) * force,
      y: -150 - Math.random() * 50, // Up first
    };
  }

  /** Toggle filter visibility for a given rarity */
  static toggleFilter(rarity: string): void {
    switch (rarity) {
      case 'normal': DroppedItem.filterSettings.showNormal = !DroppedItem.filterSettings.showNormal; break;
      case 'magic': DroppedItem.filterSettings.showMagic = !DroppedItem.filterSettings.showMagic; break;
      case 'rare': DroppedItem.filterSettings.showRare = !DroppedItem.filterSettings.showRare; break;
      case 'set': DroppedItem.filterSettings.showSet = !DroppedItem.filterSettings.showSet; break;
      case 'legendary': DroppedItem.filterSettings.showLegendary = !DroppedItem.filterSettings.showLegendary; break;
      case 'currency': DroppedItem.filterSettings.showCurrency = !DroppedItem.filterSettings.showCurrency; break;
      case 'quest': DroppedItem.filterSettings.showQuest = !DroppedItem.filterSettings.showQuest; break;
    }
  }

  /** Check if this item should be prominently displayed based on current filter */
  shouldDisplay(): boolean {
    const filter = DroppedItem.filterSettings;
    const isCurrency = 'currencyType' in this.item;

    if (isCurrency) {
      return filter.showCurrency;
    }

    // Check item level
    if (this.item.level < filter.minItemLevel) {
      return false;
    }

    // Check rarity
    const rarity: ItemRarity = this.item.rarity;
    switch (rarity) {
      case 'normal': return filter.showNormal;
      case 'magic': return filter.showMagic;
      case 'rare': return filter.showRare;
      case 'set': return filter.showSet;
      case 'legendary': return filter.showLegendary;
      default: return true;
    }
  }

  startCollect(targetX: number, targetY: number): void {
    this.isBeingCollected = true;
    this.collectTimer = 0;
    this.collectStartPos = { x: this.position.x, y: this.position.y };
    this.collectTargetPos = { x: targetX, y: targetY };
    this.collectScale = 1.0;

    // Emit sparkle particles at pickup location
    const color = getItemColor(this.item.rarity);
    getParticleSystem().emitBurst(this.position.x, this.position.y - 10, color, 6);
  }

  isCollectComplete(): boolean {
    return this.isBeingCollected && this.collectTimer >= this.collectDuration;
  }

  update(deltaTime: number): void {
    if (this.isBeingCollected) {
      this.collectTimer += deltaTime;
      const progress = Math.min(1, this.collectTimer / this.collectDuration);

      // Ease-in lerp toward player
      const ease = progress * progress; // quadratic ease-in
      this.position.x = this.collectStartPos.x + (this.collectTargetPos.x - this.collectStartPos.x) * ease;
      this.position.y = this.collectStartPos.y + (this.collectTargetPos.y - this.collectStartPos.y) * ease;
      this.collectScale = 1.0 - progress * 0.5; // 1.0 -> 0.5
      return;
    }

    if (!this.grounded) {
      // Apply gravity
      this.velocity.y += 500 * deltaTime;

      // Move
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;

      // Check ground
      if (this.position.y >= this.groundY) {
        this.position.y = this.groundY;
        this.grounded = true;
      }
    } else {
      // Items stay still on ground - no bobbing
      this.bobOffset = 0;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = camera.worldToScreen(this.position.x, this.position.y);
    const ctx = renderer.context;

    // Apply bob
    const renderY = screenPos.y - this.bobOffset * camera.zoom;

    // Loot filter: use LootFilter system for opacity-based filtering + level threshold
    const lootFilter = LootFilter.getInstance();
    const filterOpacity = lootFilter.getEffectiveItemOpacity(this.item.rarity, this.item.level);

    // Combine with legacy per-rarity boolean filter
    const passesLegacyFilter = this.shouldDisplay();
    const effectiveOpacity = passesLegacyFilter ? filterOpacity : Math.min(0.15, filterOpacity);

    // Skip rendering entirely if fully hidden
    if (effectiveOpacity <= 0) return;

    const isFullyVisible = effectiveOpacity >= 1.0;

    if (!isFullyVisible) {
      ctx.save();
      ctx.globalAlpha = effectiveOpacity;
      if (effectiveOpacity < 0.5) {
        ctx.translate(screenPos.x, renderY);
        ctx.scale(0.7, 0.7);
        ctx.translate(-screenPos.x, -renderY);
      }
    }

    // Apply collect animation scale
    if (this.isBeingCollected && isFullyVisible) {
      ctx.save();
      ctx.translate(screenPos.x, renderY);
      ctx.scale(this.collectScale, this.collectScale);
      ctx.translate(-screenPos.x, -renderY);
    }

    // Determine color and size based on item type
    const color = getItemColor(this.item.rarity);
    const isCurrency = 'currencyType' in this.item;

    if (isCurrency) {
      this.renderCurrency(renderer, screenPos.x, renderY, camera.zoom);
    } else {
      this.renderEquipment(renderer, screenPos.x, renderY, color, camera.zoom);
    }

    // Pulsing glow ring under legendary items
    if (isFullyVisible && this.grounded && !isCurrency && this.item.rarity === 'legendary') {
      this.renderLegendaryGlowRing(ctx, screenPos.x, renderY, color, camera.zoom);
    }

    // Glow effect for magic+ items
    if (this.item.rarity !== 'normal') {
      this.renderGlow(renderer, screenPos.x, renderY, color, camera.zoom);
    }

    // Rotating particles around sacred+ items on the ground
    if (isFullyVisible && this.grounded && !isCurrency) {
      const rarity = this.item.rarity;
      if (rarity === 'set' || rarity === 'legendary') {
        this.renderOrbitingParticles(ctx, screenPos.x, renderY, color, rarity, camera.zoom);
      }
    }

    // Rarity beam of light for rare+ items that are fully visible
    if (isFullyVisible && this.grounded && !isCurrency) {
      const rarity = this.item.rarity;
      if (rarity === 'rare' || rarity === 'set' || rarity === 'legendary') {
        this.renderRarityBeam(ctx, screenPos.x, renderY, color, rarity, camera.zoom);
      }
    }

    if (this.isBeingCollected && isFullyVisible) {
      ctx.restore();
    }

    if (!isFullyVisible) {
      ctx.restore();
    }
  }

  /** Draw a vertical beam of light above the item, colored by rarity */
  private renderRarityBeam(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    rarity: ItemRarity,
    zoom: number
  ): void {
    const time = DroppedItem.frameTime * 0.003;
    const baseAlpha = Math.sin(time) * 0.15 + 0.35;

    // Legendary items get much taller, wider, brighter beams
    const isLegendary = rarity === 'legendary';
    const isSet = rarity === 'set';
    let beamHeight: number;
    let beamWidth: number;
    let alpha: number;

    if (isLegendary) {
      beamHeight = 90 * zoom;
      beamWidth = 16 * zoom;
      alpha = baseAlpha + 0.25;
    } else if (isSet) {
      beamHeight = 55 * zoom;
      beamWidth = 12 * zoom;
      alpha = baseAlpha + 0.15;
    } else {
      beamHeight = 40 * zoom;
      beamWidth = 6 * zoom;
      alpha = baseAlpha;
    }

    // Vertical beam gradient (fades upward)
    const beamTop = y - 20 * zoom - beamHeight;
    const gradient = ctx.createLinearGradient(x, y - 20 * zoom, x, beamTop);
    gradient.addColorStop(0, color + this.alphaToHex(alpha));
    gradient.addColorStop(0.5, color + this.alphaToHex(alpha * 0.6));
    gradient.addColorStop(0.8, color + this.alphaToHex(alpha * 0.25));
    gradient.addColorStop(1, color + '00');

    ctx.fillStyle = gradient;
    ctx.fillRect(x - beamWidth / 2, beamTop, beamWidth, beamHeight);

    // Legendary: secondary outer glow beam (wider, softer)
    if (isLegendary) {
      const outerWidth = beamWidth * 2.5;
      const outerGrad = ctx.createLinearGradient(x, y - 20 * zoom, x, beamTop);
      outerGrad.addColorStop(0, color + this.alphaToHex(alpha * 0.3));
      outerGrad.addColorStop(0.5, color + this.alphaToHex(alpha * 0.12));
      outerGrad.addColorStop(1, color + '00');
      ctx.fillStyle = outerGrad;
      ctx.fillRect(x - outerWidth / 2, beamTop, outerWidth, beamHeight);
    }

    // Set/Legendary: add shimmer particles along the beam
    if (isSet || isLegendary) {
      const particleCount = isLegendary ? 7 : 4;
      for (let i = 0; i < particleCount; i++) {
        const t = (time * 0.8 + i * 0.7) % 1;
        const py = y - 20 * zoom - t * beamHeight;
        const px = x + Math.sin(time * 2 + i * 1.5) * beamWidth * 0.5;
        const particleAlpha = Math.sin(t * Math.PI) * (isLegendary ? 0.8 : 0.6);

        ctx.fillStyle = color + this.alphaToHex(particleAlpha);
        ctx.beginPath();
        ctx.arc(px, py, (isLegendary ? 2.5 : 2) * zoom, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /** Pulsing glow ring under legendary items on the ground */
  private renderLegendaryGlowRing(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    zoom: number
  ): void {
    const time = DroppedItem.frameTime * 0.003;
    const pulse = Math.sin(time * 1.5) * 0.5 + 0.5;
    const ringRadius = (22 + pulse * 8) * zoom;
    const ringAlpha = 0.2 + pulse * 0.2;

    // Outer glow ring
    ctx.beginPath();
    ctx.ellipse(x, y + 4 * zoom, ringRadius, ringRadius * 0.5, 0, 0, Math.PI * 2);
    const ringGrad = ctx.createRadialGradient(x, y + 4 * zoom, ringRadius * 0.5, x, y + 4 * zoom, ringRadius);
    ringGrad.addColorStop(0, color + this.alphaToHex(ringAlpha * 0.6));
    ringGrad.addColorStop(0.6, color + this.alphaToHex(ringAlpha * 0.3));
    ringGrad.addColorStop(1, color + '00');
    ctx.fillStyle = ringGrad;
    ctx.fill();

    // Inner ring line
    ctx.beginPath();
    ctx.ellipse(x, y + 4 * zoom, ringRadius * 0.7, ringRadius * 0.35, 0, 0, Math.PI * 2);
    ctx.strokeStyle = color + this.alphaToHex(ringAlpha * 0.5);
    ctx.lineWidth = 1.5 * zoom;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** Subtle rotating particles orbiting around sacred+ items */
  private renderOrbitingParticles(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    rarity: ItemRarity,
    zoom: number
  ): void {
    const time = DroppedItem.frameTime * 0.001;
    const isLegendary = rarity === 'legendary';
    const count = isLegendary ? 6 : 4;
    const orbitRadius = (isLegendary ? 20 : 16) * zoom;
    const particleSize = (isLegendary ? 2.2 : 1.6) * zoom;

    for (let i = 0; i < count; i++) {
      const angle = time * 1.2 + (i / count) * Math.PI * 2;
      const yOffset = Math.sin(time * 2 + i) * 3 * zoom;
      const px = x + Math.cos(angle) * orbitRadius;
      const py = y - 8 * zoom + Math.sin(angle) * orbitRadius * 0.4 + yOffset;
      const pAlpha = 0.4 + Math.sin(time * 3 + i * 1.3) * 0.3;

      // Particle glow
      ctx.globalAlpha = pAlpha * 0.3;
      ctx.beginPath();
      ctx.arc(px, py, particleSize * 2.5, 0, Math.PI * 2);
      const pGlow = ctx.createRadialGradient(px, py, 0, px, py, particleSize * 2.5);
      pGlow.addColorStop(0, color);
      pGlow.addColorStop(1, color + '00');
      ctx.fillStyle = pGlow;
      ctx.fill();

      // Particle core
      ctx.globalAlpha = pAlpha;
      ctx.beginPath();
      ctx.arc(px, py, particleSize, 0, Math.PI * 2);
      ctx.fillStyle = isLegendary ? '#ffffff' : color;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /** Convert a 0-1 alpha to a 2-char hex string for appending to color */
  private alphaToHex(alpha: number): string {
    const clamped = Math.max(0, Math.min(1, alpha));
    const hex = Math.round(clamped * 255).toString(16);
    return hex.length < 2 ? '0' + hex : hex;
  }

  private renderCurrency(renderer: Renderer, x: number, y: number, zoom: number): void {
    const item = this.item as CurrencyItem;
    const size = 8 * zoom;

    // Gold coin
    renderer.context.beginPath();
    renderer.context.arc(x, y - size, size, 0, Math.PI * 2);
    renderer.context.fillStyle = '#ffd700';
    renderer.context.fill();
    renderer.context.strokeStyle = '#b8860b';
    renderer.context.lineWidth = 2 * zoom;
    renderer.context.stroke();

    // Amount text if > 1
    if (item.stackSize > 1) {
      renderer.drawText(
        item.stackSize.toString(),
        x,
        y - size - 10 * zoom,
        {
          color: '#ffffff',
          fontSize: 10 * zoom,
          align: 'center',
          baseline: 'middle',
        }
      );
    }
  }

  private renderEquipment(
    renderer: Renderer,
    x: number,
    y: number,
    color: string,
    zoom: number
  ): void {
    const ctx = renderer.context;
    const equipItem = this.item as EquipmentItem;
    const slot = equipItem.slot;

    // Draw item icon based on slot type
    this.renderSlotIcon(ctx, x, y, slot, color, zoom);

    // Draw item name in a rectangle label below
    const fontSize = Math.max(10, 12 * zoom);
    ctx.font = `bold ${fontSize}px Arial`;
    const textWidth = ctx.measureText(this.item.name).width;
    const padding = 4 * zoom;
    const labelHeight = fontSize + padding * 2;
    const labelWidth = textWidth + padding * 2;
    const labelY = y + 5 * zoom;

    // Label background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(x - labelWidth / 2, labelY, labelWidth, labelHeight);

    // Label border with rarity color
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5 * zoom;
    ctx.strokeRect(x - labelWidth / 2, labelY, labelWidth, labelHeight);

    // Item name text
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.item.name, x, labelY + labelHeight / 2);
  }

  private renderSlotIcon(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    slot: EquipmentSlot,
    color: string,
    zoom: number
  ): void {
    // 1. Bitmap icon from loaded assets (custom folder or 7soul)
    if (this.item.icon && DroppedItem.assetLoader) {
      const img = DroppedItem.assetLoader.getImage(`item_icon:${this.item.icon}`);
      if (img) {
        const size = 24 * zoom;
        ctx.drawImage(img, x - size / 2, y - size, size, size);
        return;
      }
    }

    // 2. Procedural canvas icon fallback
    const procIcon = getProceduralIcon(slot, this.item.rarity);
    if (procIcon) {
      const size = 24 * zoom;
      ctx.drawImage(procIcon, x - size / 2, y - size, size, size);
      return;
    }

    // 3. Legacy shape fallback
    const size = 14 * zoom;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2 * zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (slot) {
      case 'weapon':
        // Sword icon
        ctx.beginPath();
        // Blade
        ctx.moveTo(x, y - size * 1.5);
        ctx.lineTo(x + size * 0.3, y - size * 0.3);
        ctx.lineTo(x, y);
        ctx.lineTo(x - size * 0.3, y - size * 0.3);
        ctx.closePath();
        ctx.fill();
        // Guard
        ctx.beginPath();
        ctx.moveTo(x - size * 0.5, y - size * 0.2);
        ctx.lineTo(x + size * 0.5, y - size * 0.2);
        ctx.stroke();
        // Handle
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y + size * 0.4);
        ctx.stroke();
        break;

      case 'head':
        // Helmet icon
        ctx.beginPath();
        ctx.arc(x, y - size * 0.5, size * 0.6, Math.PI, 0);
        ctx.lineTo(x + size * 0.6, y);
        ctx.lineTo(x - size * 0.6, y);
        ctx.closePath();
        ctx.fill();
        // Visor line
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1 * zoom;
        ctx.beginPath();
        ctx.moveTo(x - size * 0.4, y - size * 0.3);
        ctx.lineTo(x + size * 0.4, y - size * 0.3);
        ctx.stroke();
        break;

      case 'chest':
        // Armor/chestplate icon
        ctx.beginPath();
        ctx.moveTo(x - size * 0.5, y - size);
        ctx.lineTo(x + size * 0.5, y - size);
        ctx.lineTo(x + size * 0.6, y - size * 0.5);
        ctx.lineTo(x + size * 0.4, y);
        ctx.lineTo(x, y + size * 0.2);
        ctx.lineTo(x - size * 0.4, y);
        ctx.lineTo(x - size * 0.6, y - size * 0.5);
        ctx.closePath();
        ctx.fill();
        break;

      case 'gloves':
        // Glove icon
        ctx.beginPath();
        ctx.moveTo(x - size * 0.4, y - size * 0.8);
        ctx.lineTo(x + size * 0.4, y - size * 0.8);
        ctx.lineTo(x + size * 0.4, y - size * 0.2);
        ctx.lineTo(x + size * 0.2, y);
        ctx.lineTo(x - size * 0.2, y);
        ctx.lineTo(x - size * 0.4, y - size * 0.2);
        ctx.closePath();
        ctx.fill();
        // Fingers
        ctx.fillRect(x - size * 0.35, y - size * 1.1, size * 0.15, size * 0.3);
        ctx.fillRect(x - size * 0.1, y - size * 1.2, size * 0.2, size * 0.4);
        ctx.fillRect(x + size * 0.2, y - size * 1.1, size * 0.15, size * 0.3);
        break;

      case 'boots':
        // Boot icon
        ctx.beginPath();
        ctx.moveTo(x - size * 0.3, y - size);
        ctx.lineTo(x + size * 0.1, y - size);
        ctx.lineTo(x + size * 0.1, y - size * 0.3);
        ctx.lineTo(x + size * 0.5, y - size * 0.3);
        ctx.lineTo(x + size * 0.5, y);
        ctx.lineTo(x - size * 0.3, y);
        ctx.closePath();
        ctx.fill();
        break;

      case 'belt':
        // Belt icon
        ctx.fillRect(x - size * 0.6, y - size * 0.6, size * 1.2, size * 0.4);
        // Buckle
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5 * zoom;
        ctx.strokeRect(x - size * 0.15, y - size * 0.55, size * 0.3, size * 0.3);
        break;

      case 'offhand':
        // Shield icon
        ctx.beginPath();
        ctx.moveTo(x, y - size);
        ctx.lineTo(x + size * 0.5, y - size * 0.7);
        ctx.lineTo(x + size * 0.5, y - size * 0.2);
        ctx.lineTo(x, y + size * 0.2);
        ctx.lineTo(x - size * 0.5, y - size * 0.2);
        ctx.lineTo(x - size * 0.5, y - size * 0.7);
        ctx.closePath();
        ctx.fill();
        break;

      case 'amulet':
        // Amulet/necklace icon
        ctx.beginPath();
        ctx.arc(x, y - size * 0.3, size * 0.4, 0, Math.PI * 2);
        ctx.fill();
        // Chain hint
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5 * zoom;
        ctx.beginPath();
        ctx.arc(x, y - size * 0.8, size * 0.3, 0.3 * Math.PI, 0.7 * Math.PI);
        ctx.stroke();
        break;

      case 'ring1':
      case 'ring2':
        // Ring icon
        ctx.beginPath();
        ctx.arc(x, y - size * 0.4, size * 0.4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y - size * 0.4, size * 0.2, 0, Math.PI * 2);
        ctx.fill();
        break;

      case 'cape':
        // Cape icon
        ctx.beginPath();
        ctx.moveTo(x - size * 0.5, y - size);
        ctx.lineTo(x + size * 0.5, y - size);
        ctx.quadraticCurveTo(x + size * 0.6, y - size * 0.3, x + size * 0.3, y);
        ctx.lineTo(x - size * 0.3, y);
        ctx.quadraticCurveTo(x - size * 0.6, y - size * 0.3, x - size * 0.5, y - size);
        ctx.closePath();
        ctx.fill();
        break;

      default:
        // Generic item - diamond shape
        ctx.beginPath();
        ctx.moveTo(x, y - size);
        ctx.lineTo(x + size * 0.5, y - size * 0.5);
        ctx.lineTo(x, y);
        ctx.lineTo(x - size * 0.5, y - size * 0.5);
        ctx.closePath();
        ctx.fill();
    }
  }

  private renderGlow(
    renderer: Renderer,
    x: number,
    y: number,
    color: string,
    zoom: number
  ): void {
    const pulse = 0.5 + 0.5 * Math.sin(DroppedItem.frameTime * 0.005);
    const glowRadius = (20 + pulse * 10) * zoom;

    const gradient = renderer.context.createRadialGradient(
      x, y - 10 * zoom, 0,
      x, y - 10 * zoom, glowRadius
    );
    gradient.addColorStop(0, color + '40');
    gradient.addColorStop(1, color + '00');

    renderer.context.beginPath();
    renderer.context.arc(x, y - 10 * zoom, glowRadius, 0, Math.PI * 2);
    renderer.context.fillStyle = gradient;
    renderer.context.fill();
  }

  isInPickupRange(playerX: number, playerY: number): boolean {
    const dx = this.position.x - playerX;
    const dy = this.position.y - playerY;
    return dx * dx + dy * dy <= this.pickupRadius * this.pickupRadius && this.grounded;
  }

  getDisplayName(): string {
    if ('currencyType' in this.item) {
      const currency = this.item as CurrencyItem;
      return `${currency.stackSize} ${currency.name}`;
    }
    return this.item.name;
  }
}
